import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  CreateRequest,
  GetRequest,
  GetRequestList,
  GetKindsTree,
  RateRequest,
  ReserveRequestFile,
  GetRequestFileSharedId,
} from 'app/api/requests';
import { GetFileFromFilesMS, SaveFileToFilesMS } from 'app/api/files';
import { showWarning } from 'app/store/root-slice';
import { RootState } from 'app/store/store';
import { formatDateToISO } from 'app/utils/getters';
import moment from 'moment';
import { IBlankRequest, IRequest } from './types';
import { IPhone } from 'app/types';
import { getTenantPhones } from '../profile/profile-slice';

export type ListType = 'all' | 'area' | 'house';

export interface RequestFilter {
  limit: number;
  offset: number;
  house_requests: boolean;
  tenant_requests: boolean;
}

export interface InitialState {
  statuses: {
    loaded: {
      all: boolean;
      area: boolean;
      house: boolean;
    };
    onLoad: {
      all: boolean;
      area: boolean;
      house: boolean;
    };
    show: ListType;
  };
  requests: {
    selected: IRequest;
    all: IRequest[];
    area: IRequest[];
    house: IRequest[];
    counts: {
      all: number;
      area: number;
      house: number;
    };
  };
  filters: {
    all: RequestFilter;
    area: RequestFilter;
    house: RequestFilter;
  };
  blankRequest: IBlankRequest;
  commonPhones: IPhone[];
  // TODO проверить что может быть в ответе и типизировать
  kindsTree: { [propName: string]: any };
  isLoadedKindsTree: boolean;
}

export const initialState: InitialState = {
  statuses: {
    loaded: {
      all: false,
      area: false,
      house: false,
    },
    onLoad: {
      all: false,
      area: false,
      house: false,
    },
    show: 'house',
  },
  requests: {
    selected: {} as IRequest,
    all: [],
    area: [],
    house: [],
    counts: {
      all: 0,
      area: 0,
      house: 0,
    },
  },
  filters: {
    all: {
      // В данный момент это "Мои"
      limit: 10,
      offset: 0,
      tenant_requests: true,
      house_requests: false,
    },
    area: {
      limit: 10,
      offset: 0, // Пока не используется
      tenant_requests: true,
      house_requests: false,
    },
    house: {
      // В данный момент это "Все"
      limit: 10,
      offset: 0,
      tenant_requests: true,
      house_requests: true,
    },
  },
  blankRequest: {
    _type: null,
    description: '',
    execution: {
      desired_start_at: '',
      desired_end_at: '',
    },
    category: '',
    subcategory: null,
    phones: [],
  },
  commonPhones: [],
  kindsTree: {},
  isLoadedKindsTree: false,
};

export const getRequestsList = createAsyncThunk<any>(
  'requests/getRequestsList',
  async (listType, { rejectWithValue, dispatch, getState }) => {
    const state = getState() as RootState;
    try {
      const data = await GetRequestList(
        state.requests.filters[state.requests.statuses.show]
      );

      return {
        list: data.requests || [],
        type: state.requests.statuses.show,
        count: data.count || 0,
      };
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const getRequest = createAsyncThunk<any, string, any>(
  'requests/getRequest',
  async (requestId, { rejectWithValue, dispatch, getState }) => {
    try {
      return await GetRequest(requestId);
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const rateRequest = createAsyncThunk<
  any,
  { requestId: string; score: any },
  any
>(
  'requests/rateRequest',
  async (data, { rejectWithValue, dispatch, getState }) => {
    try {
      return await RateRequest(data.requestId, data.score);
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const createRequest = createAsyncThunk<any, IBlankRequest, any>(
  'requests/createRequest',
  async (request, { rejectWithValue, dispatch, getState }) => {
    try {
      return await CreateRequest({
        ...request,
        phones: request.phones.map((item) => {
          return {
            type: item.type || item?.phone_type,
            add: item.add,
            number: item.code + item.number,
            added_by_tenant: item.added_by_tenant,
          };
        }),
        execution: {
          desired_start_at: formatDateToISO(
            moment(request.execution.desired_start_at)
          ),
          desired_end_at: formatDateToISO(
            moment(request.execution.desired_start_at).add(15, 'minutes')
          ), // Устанавливаю одинаковую специально
        },
      });
    } catch (e) {
      dispatch(showWarning());
      return rejectWithValue(e);
    }
  }
);

export const reserveRequestFile = createAsyncThunk<
  any,
  { filename: string },
  any
>('requests/reserveRequestFile', async ({ filename }, { rejectWithValue }) => {
  try {
    return await ReserveRequestFile({ filename });
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const uploadRequestFile = createAsyncThunk<
  any,
  { shared_id: string; file: File },
  any
>(
  'requests/uploadRequestFile',
  async ({ shared_id, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('importance', 'low');

      return await SaveFileToFilesMS(shared_id, formData);
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const downloadRequestFile = createAsyncThunk<
  any,
  { file_ids: string; filenames: string },
  any
>(
  'requests/downloadRequestFile',
  async ({ file_ids, filenames }, { rejectWithValue }) => {
    try {
      const ids = await GetRequestFileSharedId({ file_ids, filenames });
      return await GetFileFromFilesMS(Object.values(ids)[0]);
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const getKindsTree = createAsyncThunk(
  'requests/getKindsTree',
  async (_, { rejectWithValue, getState }) => {
    try {
      return await GetKindsTree();
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const requestsSlice = createSlice({
  name: 'requests',
  initialState,
  reducers: {
    clearErrors: (state) => {},
    switchListType: (state, action) => {
      state.statuses.show = action.payload;
    },
    selectRequest: (state, action: PayloadAction<IRequest>) => {
      state.requests.selected = action.payload;
    },
    clearCurrentRequest: (state) => {
      state.requests.selected = initialState.requests.selected;
    },
    resetStore: (state) => ({
      ...initialState,
      statuses: {
        ...initialState.statuses,
        show: state.statuses.show, // Это нужно, что бы неуправляемый buttonsTabs оставался актуальным
        loaded: {
          ...state.statuses.loaded,
          [state.statuses.show]: false,
        },
      },
      blankRequest: {
        ...initialState.blankRequest,
      },
      commonPhones: state.commonPhones,
    }),
    resetBlankRequest: (state) => ({
      ...state,
      blankRequest: {
        ...initialState.blankRequest,
        phones: state.commonPhones,
      },
    }),
    addPhone: (state, action) => {
      state.blankRequest.phones.push(action.payload);
    },
    removePhone: (state, action) => {
      state.blankRequest.phones.splice(action.payload, 1);
    },
    addDate: (state, action) => {
      state.blankRequest.execution.desired_start_at = action.payload;
    },
    typeChoice: (state, action) => {
      state.blankRequest._type = action.payload;
    },
    addDescription: (state, action) => {
      state.blankRequest.description = action.payload;
    },
    setCategory: (state, action) => {
      state.blankRequest.category = action.payload;
      state.blankRequest.subcategory = null;
    },
    setSubCategory: (state, action) => {
      state.blankRequest.subcategory = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Получение списка тикетов
    builder.addCase(getRequestsList.pending, (state, action) => {
      // state.statuses.loaded[state.statuses.show] = true;
      state.statuses.onLoad[state.statuses.show] = true;
    });

    builder.addCase(getRequestsList.rejected, (state, action) => {
      state.statuses.onLoad[state.statuses.show] = false;
    });

    builder.addCase(
      getRequestsList.fulfilled,
      (
        state,
        action: PayloadAction<{
          list: IRequest[];
          type: ListType;
          count: number;
        }>
      ) => {
        const { type, list, count } = action.payload;
        state.requests[type] = [...state.requests[type], ...list];
        state.requests.counts[type] = count;
        state.filters[type].offset += 10;
        state.statuses.onLoad[type] = false;
        state.statuses.loaded[type] = true;
      }
    );

    builder.addCase(rateRequest.fulfilled, (state, action) => {
      const types: ListType[] = ['all', 'area', 'house'];
      let ratedRequest = state.requests.selected;

      types.forEach((type) => {
        state.requests[type] = state.requests[type].map((request) => {
          if (request._id !== action.payload.request_id) {
            return request;
          } else {
            ratedRequest = {
              ...request,
              execution: {
                ...request.execution,
                rates: [...request.execution.rates, action.payload],
              },
            };

            return ratedRequest;
          }
        });

        state.requests.selected = ratedRequest;
      });
    });

    builder.addCase(getRequest.fulfilled, (state, action) => {
      const types: ListType[] = ['all', 'area', 'house'];

      types.forEach((type) => {
        state.requests[type] = state.requests[type].map((request) => {
          if (request._id !== action.payload._id) {
            return request;
          } else {
            return action.payload;
          }
        });
      });
    });

    builder.addCase(getTenantPhones.fulfilled, (state, { payload }) => {
      state.blankRequest.phones = payload;
      state.commonPhones = payload;
    });

    builder.addCase(getKindsTree.fulfilled, (state, { payload }) => {
      state.kindsTree = payload.categories;
      state.isLoadedKindsTree = true;
    });

    builder.addCase(createRequest.fulfilled, (state, { payload }) => {
      // state.statuses.loaded[state.statuses.show] = false;
    });
  },
});

export const {
  switchListType,
  selectRequest,
  resetStore,
  addPhone,
  removePhone,
  addDate,
  typeChoice,
  clearCurrentRequest,
  addDescription,
  setCategory,
  setSubCategory,
  resetBlankRequest,
} = requestsSlice.actions;

export default requestsSlice.reducer;
